import React, { useState, useEffect, useRef } from 'react';

import {
    Bell, Sun, Moon, ToggleLeft, User,
    LayoutDashboard, Megaphone, Share2, Settings, Home, BarChart2, DollarSign, Target, CheckCircle, Clock, Heart, MessageSquare, Repeat2,
    Users, ExternalLink, Flag, Image, Film, MessageCircle
} from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import PerformanceTrendsChart from '../../components/Charts/PerformanceTrendsChart';

// --- Reusable Dummy Data (Adjusted and extended for richer content) ---
const commonMetricsData = {
    totalSpend: { value: '$15,840', change: 5.2, isPositive: true },
    totalImpressions: { value: '88,920', change: 5.7, isPositive: true },
    totalClicks: { value: '88,920', change: 7.4, isPositive: true },
    averageCTR: { value: '7.41%', change: 10, isPositive: true },
};

// base "All" dataset (used if Platform = All)
const analyticsChartDataAll = [
    { name: 'Day 1', Clicks: 5, Impressions: 10, Spend: 100 },
    { name: 'Day 5', Clicks: 8, Impressions: 15, Spend: 120 },
    { name: 'Day 10', Clicks: 12, Impressions: 20, Spend: 150 },
    { name: 'Day 15', Clicks: 15, Impressions: 25, Spend: 180 },
    { name: 'Day 20', Clicks: 18, Impressions: 30, Spend: 200 },
    { name: 'Day 25', Clicks: 22, Impressions: 35, Spend: 220 },
    { name: 'Day 30', Clicks: 25, Impressions: 40, Spend: 250 },
];

// per-platform datasets (demo values)
const analyticsChartDataFacebook = [
    { name: 'Day 1', Clicks: 3, Impressions: 7, Spend: 80 },
    { name: 'Day 5', Clicks: 6, Impressions: 12, Spend: 100 },
    { name: 'Day 10', Clicks: 9, Impressions: 16, Spend: 130 },
    { name: 'Day 15', Clicks: 13, Impressions: 22, Spend: 160 },
    { name: 'Day 20', Clicks: 15, Impressions: 26, Spend: 190 },
    { name: 'Day 25', Clicks: 17, Impressions: 30, Spend: 210 },
    { name: 'Day 30', Clicks: 20, Impressions: 34, Spend: 230 },
];

const analyticsChartDataInstagram = [
    { name: 'Day 1', Clicks: 4, Impressions: 9, Spend: 90 },
    { name: 'Day 5', Clicks: 7, Impressions: 13, Spend: 110 },
    { name: 'Day 10', Clicks: 11, Impressions: 19, Spend: 140 },
    { name: 'Day 15', Clicks: 14, Impressions: 24, Spend: 170 },
    { name: 'Day 20', Clicks: 16, Impressions: 28, Spend: 190 },
    { name: 'Day 25', Clicks: 19, Impressions: 33, Spend: 210 },
    { name: 'Day 30', Clicks: 23, Impressions: 37, Spend: 240 },
];

const analyticsChartDataLinkedIn = [
    { name: 'Day 1', Clicks: 2, Impressions: 6, Spend: 70 },
    { name: 'Day 5', Clicks: 5, Impressions: 10, Spend: 90 },
    { name: 'Day 10', Clicks: 8, Impressions: 14, Spend: 120 },
    { name: 'Day 15', Clicks: 10, Impressions: 18, Spend: 140 },
    { name: 'Day 20', Clicks: 13, Impressions: 22, Spend: 160 },
    { name: 'Day 25', Clicks: 15, Impressions: 27, Spend: 180 },
    { name: 'Day 30', Clicks: 17, Impressions: 31, Spend: 200 },
];

const analyticsChartDataYouTube = [
    { name: 'Day 1', Clicks: 6, Impressions: 12, Spend: 120 },
    { name: 'Day 5', Clicks: 9, Impressions: 17, Spend: 140 },
    { name: 'Day 10', Clicks: 13, Impressions: 22, Spend: 170 },
    { name: 'Day 15', Clicks: 16, Impressions: 27, Spend: 190 },
    { name: 'Day 20', Clicks: 19, Impressions: 32, Spend: 210 },
    { name: 'Day 25', Clicks: 22, Impressions: 37, Spend: 230 },
    { name: 'Day 30', Clicks: 25, Impressions: 42, Spend: 250 },
];

const budgetAllocationData = [
    { name: 'Pack A', value: 40, color: '#3b82f6' }, // Blue
    { name: 'Pack B', value: 30, color: '#10b981' }, // Green
    { name: 'Pack C', value: 20, color: '#f59e0b' }, // Yellow/Orange
    { name: 'Pack D', value: 10, color: '#ef4444' }, // Red
];

// Post-specific data
const postPerformanceData = [
    { name: 'Post 1', Likes: 1200, Comments: 250, Shares: 80, Reach: 15000 },
    { name: 'Post 2', Likes: 850, Comments: 180, Shares: 50, Reach: 10000 },
    { name: 'Post 3', Likes: 1500, Comments: 300, Shares: 100, Reach: 20000 },
    { name: 'Post 4', Likes: 900, Comments: 100, Shares: 40, Reach: 12000 },
    { name: 'Post 5', Likes: 1100, Comments: 220, Shares: 70, Reach: 16000 },
];

const topPostsData = [
    { id: 1, title: 'Summer Sale Announcement!', type: 'Image', reach: '25K', engagement: '1.2K', date: '2023-07-15' },
    { id: 2, title: 'New Product Launch Video', type: 'Video', reach: '30K', engagement: '2.5K', date: '2023-07-10' },
    { id: 3, title: 'Behind the Scenes: Our Team', type: 'Image', reach: '18K', engagement: '900', date: '2023-07-20' },
];

// Campaign-specific data
const campaignSummaryData = [
    { metric: 'Active Campaigns', value: 5, icon: Megaphone },
    { metric: 'Completed Campaigns', value: 12, icon: CheckCircle },
    { metric: 'Total Budget Used', value: '$25,000', icon: DollarSign },
    { metric: 'Avg. Conversion Rate', value: '3.8%', icon: Target },
];

const campaignPerformanceChartData = [
    { name: 'Week 1', Conversions: 50, Clicks: 500, Budget: 1000 },
    { name: 'Week 2', Conversions: 70, Clicks: 650, Budget: 1200 },
    { name: 'Week 3', Conversions: 60, Clicks: 580, Budget: 1100 },
    { name: 'Week 4', Conversions: 85, Clicks: 720, Budget: 1300 },
];

const recentCampaignsData = [
    { id: 1, name: 'Q3 Product Awareness', status: 'Active', conversions: 230, spend: '$5,000', endDate: '2023-09-30' },
    { id: 2, name: 'Holiday Sale 2023', status: 'Paused', conversions: 180, spend: '$3,500', endDate: '2023-01-15' },
    { id: 3, name: 'New User Acquisition', status: 'Active', conversions: 400, spend: '$7,200', endDate: '2023-10-31' },
];

// --- Sub-Components ---

const PerformanceMetric = ({ title, value, change, isPositive, unit = '', icon: Icon }) => (
    <div className="marketing-dashboard-ui-metric-card">
        <div className="marketing-dashboard-ui-metric-header">
            {Icon && <Icon size={24} className="marketing-dashboard-ui-metric-icon" />}
            <div className="marketing-dashboard-ui-metric-title">{title}</div>
        </div>
        <div className="marketing-dashboard-ui-metric-value">{value} {unit}</div>
        {change && (
            <div className={`marketing-dashboard-ui-percentage ${isPositive ? 'positive' : 'negative'}`}>
                {isPositive ? '+' : ''}{change}%
                {isPositive ? '↑' : '↓'}
            </div>
        )}
    </div>
);

const BudgetAllocationPieChart = () => (
    <div className="marketing-dashboard-ui-budget-card">
        <div className="marketing-dashboard-ui-budget-title">Budget Allocation</div>
        <div className="marketing-dashboard-ui-pie-chart-container">
            <div className="marketing-dashboard-ui-pie-chart">
                <div className="marketing-dashboard-ui-total-budget">
                    Total Budget
                    <br />
                    <strong>$15,640</strong>
                </div>
            </div>
            <div className="marketing-dashboard-ui-legend-list">
                {budgetAllocationData.map((item, index) => (
                    <div key={index} className="marketing-dashboard-ui-legend-item" style={{ '--dot-color': item.color }}>
                        {item.name} - {item.value}%
                    </div>
                ))}
            </div>
        </div>
    </div>
);

// --- Post Dashboard Content ---
const PostDashboardView = () => (
    <div className="marketing-dashboard-ui-sub-dashboard-content">

        <div className="marketing-dashboard-ui-metrics-row" style={{ marginTop: '30px' }}>
            <PerformanceMetric
                title="Total Likes"
                value={commonMetricsData.totalSpend.value}
                change={commonMetricsData.totalSpend.change}
                isPositive={commonMetricsData.totalSpend.isPositive}
                icon={Heart}
            />
            <PerformanceMetric
                title="Total Comments"
                value={commonMetricsData.totalImpressions.value}
                change={commonMetricsData.totalImpressions.change}
                isPositive={commonMetricsData.totalImpressions.isPositive}
                icon={MessageCircle}
            />
            <PerformanceMetric
                title="Total Shares"
                value={commonMetricsData.totalClicks.value}
                change={commonMetricsData.totalClicks.change}
                isPositive={commonMetricsData.totalClicks.isPositive}
                icon={Repeat2}
            />
            <PerformanceMetric
                title="Avg. Reach"
                value={commonMetricsData.averageCTR.value}
                change={commonMetricsData.averageCTR.change}
                isPositive={commonMetricsData.averageCTR.isPositive}
                icon={Users}
            />
        </div>

        <div className="marketing-dashboard-ui-charts-row" style={{ marginTop: '20px' }}>
            <div className="marketing-dashboard-ui-chart-container" style={{ flex: 2 }}>
                <div className="marketing-dashboard-ui-chart-header">
                    <p className="marketing-dashboard-ui-chart-title">Post Engagement Trends</p>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={postPerformanceData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis dataKey="name" />
                        <YAxis yAxisId="left" stroke="#ef4444" label={{ value: 'Likes/Comments', angle: -90, position: 'insideLeft' }} />
                        <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" label={{ value: 'Reach', angle: 90, position: 'insideRight' }} />
                        <Tooltip />
                        <Legend />
                        <Line yAxisId="left" type="monotone" dataKey="Likes" stroke="#ef4444" strokeWidth={2} />
                        <Line yAxisId="left" type="monotone" dataKey="Comments" stroke="#f59e0b" strokeWidth={2} />
                        <Line yAxisId="right" type="monotone" dataKey="Reach" stroke="#3b82f6" strokeWidth={2} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="marketing-dashboard-ui-budget-card" style={{ flex: 1 }}>
                <p className="marketing-dashboard-ui-budget-title">Top Performing Posts</p>
                <table className="marketing-dashboard-ui-table">
                    <thead>
                        <tr>
                            <th>Post</th>
                            <th>Type</th>
                            <th>Engagement</th>
                        </tr>
                    </thead>
                    <tbody>
                        {topPostsData.map(post => (
                            <tr key={post.id}>
                                <td>{post.title}</td>
                                <td>{post.type === 'Image' ? <Image size={16} /> : <Film size={16} />}</td>
                                <td>{post.engagement}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
);

// --- Campaign Dashboard Content ---
const CampaignDashboardView = () => (
    <div className="marketing-dashboard-ui-sub-dashboard-content">
        <div className="marketing-dashboard-ui-campaign-summary-grid">
            {campaignSummaryData.map((item, index) => (
                <div className="marketing-dashboard-ui-campaign-summary-card" key={index}>
                    {React.createElement(item.icon, { size: 24 })}
                    <h4>{item.metric}</h4>
                    <p>{item.value}</p>
                </div>
            ))}
        </div>

        <div className="marketing-dashboard-ui-charts-row" style={{ marginTop: '20px' }}>
            <div className="marketing-dashboard-ui-chart-container" style={{ flex: 2 }}>
                <div className="marketing-dashboard-ui-chart-header">
                    <p className="marketing-dashboard-ui-chart-title">Campaign Conversion & Clicks</p>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={campaignPerformanceChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis dataKey="name" />
                        <YAxis yAxisId="left" stroke="#10b981" label={{ value: 'Conversions', angle: -90, position: 'insideLeft' }} />
                        <YAxis yAxisId="right" orientation="right" stroke="#f59e0b" label={{ value: 'Clicks', angle: 90, position: 'insideRight' }} />
                        <Tooltip />
                        <Legend />
                        <Line yAxisId="left" type="monotone" dataKey="Conversions" stroke="#10b981" strokeWidth={2} />
                        <Line yAxisId="right" type="monotone" dataKey="Clicks" stroke="#f59e0b" strokeWidth={2} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="marketing-dashboard-ui-budget-card" style={{ flex: 1 }}>
                <p className="marketing-dashboard-ui-budget-title">Recent Campaigns</p>
                <table className="marketing-dashboard-ui-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Conversions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {recentCampaignsData.map(campaign => (
                            <tr key={campaign.id}>
                                <td>{campaign.name}</td>
                                <td>
                                    <span className={`marketing-dashboard-ui-status ${campaign.status.toLowerCase()}`}>
                                        {campaign.status}
                                    </span>
                                </td>
                                <td>{campaign.conversions}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
);

// --- Main Dashboard Component ---
const Dashboard = () => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [activeDashboardType, setActiveDashboardType] = useState('post');
    const [isDarkMode, setIsDarkMode] = useState(false);

    const [isPlatformDropdownOpen, setIsPlatformDropdownOpen] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState("All");

    const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
    const [selectedDateRange, setSelectedDateRange] = useState("Last 30 Days");

    const platformRef = useRef(null);
    const dateRef = useRef(null);


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (platformRef.current && !platformRef.current.contains(event.target)) {
                setIsPlatformDropdownOpen(false);
            }
            if (dateRef.current && !dateRef.current.contains(event.target)) {
                setIsDateDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    const getFilteredChartData = () => {
        switch (selectedPlatform) {
            case "Facebook":
                return analyticsChartDataFacebook;
            case "Instagram":
                return analyticsChartDataInstagram;
            case "LinkedIn":
                return analyticsChartDataLinkedIn;
            case "YouTube":
                return analyticsChartDataYouTube;
            default:
                return analyticsChartDataAll;
        }
    };


    const getDateFilteredData = (data) => {
        if (!data || !Array.isArray(data)) return data;
        switch (selectedDateRange) {
            case "Last 7 Days":
                // demo: show last 2 data points (because dataset is small)
                return data.slice(-2);
            case "Last 30 Days":
                return data;
            case "Last 90 Days":
                // demo: repeat data to simulate longer range (you should replace with real data)
                return [...data, ...data];
            case "This Year":
                return data;
            default:
                return data;
        }
    };

    // final data fed to chart
    const filteredChartData = getDateFilteredData(getFilteredChartData());

    const togglePlatformDropdown = () => {
        setIsPlatformDropdownOpen(prev => !prev);
        setIsDateDropdownOpen(false);
    };
    const toggleDateDropdown = () => {
        setIsDateDropdownOpen(prev => !prev);
        setIsPlatformDropdownOpen(false);
    };

    const handlePlatformSelect = (platform) => {
        setSelectedPlatform(platform);
        setIsPlatformDropdownOpen(false);
    };

    const handleDateRangeSelect = (range) => {
        setSelectedDateRange(range);
        setIsDateDropdownOpen(false);
    };

    return (
        <div className="marketing-dashboard-ui-main-content">
            <div className="marketing-dashboard-ui-section-header">
                <div className="marketing-dashboard-ui-toggle-group">
                    <button
                        className={`marketing-dashboard-ui-toggle-btn ${activeDashboardType === 'post' ? 'active' : ''}`}
                        onClick={() => setActiveDashboardType('post')}
                    >
                        Post
                    </button>
                    <button
                        className={`marketing-dashboard-ui-toggle-btn ${activeDashboardType === 'campaign' ? 'active' : ''}`}
                        onClick={() => setActiveDashboardType('campaign')}
                    >
                        Campaign
                    </button>
                </div>
            </div>

            <div className="marketing-dashboard-ui-dashboard-content-area">
                
              
                {activeDashboardType === 'post' ? (
                    <PostDashboardView />
                ) : (
                    <CampaignDashboardView />
                )}

                <div className="marketing-dashboard-ui-trends-header">
                    <div className="marketing-dashboard-ui-trends-title">Performance Trends</div>
                    <div className="marketing-dashboard-ui-controls">


                        <div className="dashboard-dropdown" ref={platformRef}>
                            <button
                                className="marketing-dashboard-ui-platform-btn"
                                onClick={togglePlatformDropdown}
                            >
                                Platform: {selectedPlatform}
                            </button>
                            {isPlatformDropdownOpen && (
                                <div className="dashboard-dropdown-menu">
                                    {["All", "Facebook", "Instagram", "LinkedIn", "YouTube"].map((platform) => (
                                        <div
                                            key={platform}
                                            className="dashboard-dropdown-item"
                                            onClick={() => handlePlatformSelect(platform)}
                                        >
                                            {platform}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>


                        <div className="dashboard-dropdown" ref={dateRef}>
                            <button
                                className="marketing-dashboard-ui-date-range"
                                onClick={toggleDateDropdown}
                            >
                                Date Range: {selectedDateRange}
                            </button>
                            {isDateDropdownOpen && (
                                <div className="dashboard-dropdown-menu">
                                    {["Last 7 Days", "Last 30 Days", "Last 90 Days", "This Year"].map((range) => (
                                        <div
                                            key={range}
                                            className="dashboard-dropdown-item"
                                            onClick={() => handleDateRangeSelect(range)}
                                        >
                                            {range}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="marketing-dashboard-ui-charts-row">
                    <div className="marketing-dashboard-ui-chart-container" >
                        <div className="marketing-dashboard-ui-chart-header">
                            <p className="marketing-dashboard-ui-chart-title">Clicks & Impressions Over Time</p>
                            <div className="marketing-dashboard-ui-legend">
                                <span className="marketing-dashboard-ui-legend-item" style={{ '--dot-color': '#3b82f6' }}>Clicks</span>
                                <span className="marketing-dashboard-ui-legend-item" style={{ '--dot-color': '#10b981' }}>Impressions</span>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                
                        <LineChart data={filteredChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                <XAxis dataKey="name" />
                                <YAxis yAxisId="left" stroke="#3b82f6" label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                                <YAxis yAxisId="right" orientation="right" stroke="#10b981" label={{ value: 'Spend ($)', angle: 90, position: 'insideRight' }} />
                                <Tooltip />
                                <Legend />
                                <Line yAxisId="left" type="monotone" dataKey="Clicks" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                                <Line yAxisId="left" type="monotone" dataKey="Impressions" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                            </LineChart> 

                        </ResponsiveContainer>
                    </div>
                    <BudgetAllocationPieChart />
                </div>
            </div>
        </div>
    );
};

export default Dashboard;